import React, { useState, useEffect } from 'react';
import { Crop, Download, FileText } from 'lucide-react';
import { Button, Card, FileUploadZone, ProgressBar, Alert, Badge, PDFPreview, ToolActionButtons } from '../shared';
import { PDFDocument } from 'pdf-lib';
import { saveAs } from 'file-saver';
import { toast } from 'react-toastify';
import '../../styles/design-system.css';

const CropPDF = () => {
    const [file, setFile] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [progress, setProgress] = useState(0);
    const [pageCount, setPageCount] = useState(0);
    const [cropMargins, setCropMargins] = useState({ top: 50, bottom: 50, left: 50, right: 50 });
    const [applyToAll, setApplyToAll] = useState(true);
    const [previewBlob, setPreviewBlob] = useState(null);
    const [previewFilename, setPreviewFilename] = useState('');
    const [showPreview, setShowPreview] = useState(false);

    const handleFileSelected = async (files) => {
        if (files && files.length > 0) {
            const selectedFile = files[0];
            setFile(selectedFile);

            try {
                const arrayBuffer = await selectedFile.arrayBuffer();
                const pdf = await PDFDocument.load(arrayBuffer);
                setPageCount(pdf.getPageCount());
                toast.success(`PDF loaded: ${pdf.getPageCount()} pages`);
            } catch (error) {
                toast.error(`Failed to load PDF: ${error.message}`);
            }
        }
    };

    const cropPDF = async () => {
        if (!file) return;

        try {
            setIsProcessing(true);
            setProgress(10);

            const arrayBuffer = await file.arrayBuffer();
            const pdfDoc = await PDFDocument.load(arrayBuffer);

            setProgress(30);

            const pages = pdfDoc.getPages();

            for (let i = 0; i < pages.length; i++) {
                const page = pages[i];
                const { width, height } = page.getSize();

                // Set crop box
                page.setCropBox(
                    cropMargins.left,
                    cropMargins.bottom,
                    width - cropMargins.left - cropMargins.right,
                    height - cropMargins.top - cropMargins.bottom
                );

                setProgress(30 + ((i + 1) / pages.length) * 60);
            }

            setProgress(95);

            const pdfBytes = await pdfDoc.save();
            const blob = new Blob([pdfBytes], { type: 'application/pdf' });

            setProgress(100);

            const fileName = file.name.replace(/\.pdf$/i, '');
            const filename = `${fileName}_cropped.pdf`;

            // Set preview
            setPreviewBlob(blob);
            setPreviewFilename(filename);

            toast.success('PDF cropped! Opening preview...');

            setTimeout(() => setProgress(0), 1000);

        } catch (error) {
            console.error('Error:', error);
            toast.error(`Failed to crop PDF: ${error.message}`);
        } finally {
            setIsProcessing(false);
        }
    };

    // Generate live preview when crop margins change
    const generateLivePreview = async () => {
        if (!file) {
            setPreviewBlob(null);
            return;
        }

        try {
            const arrayBuffer = await file.arrayBuffer();
            const pdfDoc = await PDFDocument.load(arrayBuffer);
            const pages = pdfDoc.getPages();

            for (let i = 0; i < pages.length; i++) {
                const page = pages[i];
                const { width, height } = page.getSize();

                page.setCropBox(
                    cropMargins.left,
                    cropMargins.bottom,
                    width - cropMargins.left - cropMargins.right,
                    height - cropMargins.top - cropMargins.bottom
                );
            }

            const pdfBytes = await pdfDoc.save();
            const blob = new Blob([pdfBytes], { type: 'application/pdf' });
            const fileName = file.name.replace(/\.pdf$/i, '');
            const filename = `${fileName}_cropped.pdf`;
            
            setPreviewBlob(blob);
            setPreviewFilename(filename);
        } catch (error) {
            console.error('Preview generation error:', error);
            setPreviewBlob(null);
        }
    };

    // Auto-update preview when crop margins change
    useEffect(() => {
        if (file) {
            const timeoutId = setTimeout(() => {
                generateLivePreview();
            }, 500);
            return () => clearTimeout(timeoutId);
        }
    }, [cropMargins, applyToAll, file]);

    return (
        <div>
            {/* Action Buttons */}
            <ToolActionButtons
                onAdd={() => {
                    document.querySelector('input[type="file"]')?.click();
                }}
                notificationCount={file ? 1 : 0}
            />

            <div className="tool-header">
                <h1 className="tool-title">
                    <Crop size={36} style={{ display: 'inline', marginRight: 'var(--spacing-3)', color: 'var(--color-accent)' }} />
                    Crop PDF
                </h1>
                <p className="tool-description">
                    Crop PDF pages by setting custom margins. Remove unwanted borders and whitespace.
                </p>
            </div>

            {!file ? (
                <FileUploadZone
                    onFilesSelected={handleFileSelected}
                    acceptedTypes={['application/pdf']}
                    multiple={false}
                    title="Drop PDF file here"
                    description="or click to browse"
                />
            ) : (
                <Alert variant="success" title="PDF Loaded">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
                            <FileText size={16} />
                            <span>{file.name}</span>
                            {pageCount > 0 && <Badge variant="info">{pageCount} pages</Badge>}
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => { setFile(null); setPageCount(0); }}>
                            Change PDF
                        </Button>
                    </div>
                </Alert>
            )}

            {file && (
                <>
                    <div style={{ marginTop: 'var(--spacing-6)' }}>
                        <Card>
                            <h3 style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--font-bold)', marginBottom: 'var(--spacing-4)' }}>
                                Crop Margins (in points)
                            </h3>

                            <div className="grid-2" style={{ marginBottom: 'var(--spacing-4)' }}>
                                <div>
                                    <label className="label">Top Margin</label>
                                    <input
                                        type="number"
                                        value={cropMargins.top}
                                        onChange={(e) => setCropMargins({ ...cropMargins, top: parseInt(e.target.value) || 0 })}
                                        min="0"
                                        className="input"
                                    />
                                </div>

                                <div>
                                    <label className="label">Bottom Margin</label>
                                    <input
                                        type="number"
                                        value={cropMargins.bottom}
                                        onChange={(e) => setCropMargins({ ...cropMargins, bottom: parseInt(e.target.value) || 0 })}
                                        min="0"
                                        className="input"
                                    />
                                </div>

                                <div>
                                    <label className="label">Left Margin</label>
                                    <input
                                        type="number"
                                        value={cropMargins.left}
                                        onChange={(e) => setCropMargins({ ...cropMargins, left: parseInt(e.target.value) || 0 })}
                                        min="0"
                                        className="input"
                                    />
                                </div>

                                <div>
                                    <label className="label">Right Margin</label>
                                    <input
                                        type="number"
                                        value={cropMargins.right}
                                        onChange={(e) => setCropMargins({ ...cropMargins, right: parseInt(e.target.value) || 0 })}
                                        min="0"
                                        className="input"
                                    />
                                </div>
                            </div>

                            <label style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--spacing-2)',
                                padding: 'var(--spacing-3)',
                                backgroundColor: 'var(--color-surface-elevated)',
                                borderRadius: 'var(--radius-md)',
                                cursor: 'pointer'
                            }}>
                                <input
                                    type="checkbox"
                                    checked={applyToAll}
                                    onChange={(e) => setApplyToAll(e.target.checked)}
                                    style={{ width: '18px', height: '18px' }}
                                />
                                <span style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-medium)' }}>
                                    Apply to all pages
                                </span>
                            </label>
                        </Card>
                    </div>

                    {isProcessing && (
                        <div style={{ marginTop: 'var(--spacing-6)' }}>
                            <ProgressBar value={progress} striped showLabel />
                        </div>
                    )}

                    <div style={{ marginTop: 'var(--spacing-6)', display: 'flex', gap: 'var(--spacing-3)' }}>
                        {previewBlob && (
                            <Button
                                variant="secondary"
                                size="lg"
                                onClick={() => setShowPreview(true)}
                                style={{ flex: 1 }}
                            >
                                <FileText size={20} style={{ marginRight: 'var(--spacing-2)' }} />
                                View Preview
                            </Button>
                        )}
                        <Button
                            variant="primary"
                            size="lg"
                            onClick={cropPDF}
                            disabled={isProcessing}
                            loading={isProcessing}
                            style={{ flex: previewBlob ? 1 : 'none', width: previewBlob ? 'auto' : '100%' }}
                            fullWidth={!previewBlob}
                        >
                            <Download size={20} />
                            Crop PDF
                        </Button>
                    </div>

                    <Alert variant="info" style={{ marginTop: 'var(--spacing-6)' }}>
                        <strong>Tip:</strong> 1 point = 1/72 inch. Standard margins are 36-72 points (0.5-1 inch).
                    </Alert>
                </>
            )}

            {/* PDF Preview Modal */}
            {previewBlob && showPreview && (
                <PDFPreview
                    pdfBlob={previewBlob}
                    filename={previewFilename}
                    onClose={() => setShowPreview(false)}
                    onDownload={() => {
                        if (previewBlob && previewFilename) {
                            saveAs(previewBlob, previewFilename);
                            toast.success('Download started!');
                        }
                    }}
                />
            )}
        </div>
    );
};

export default CropPDF;
